import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  MenuItem,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import StyleIcon from "@mui/icons-material/Style";
import AddIcon from "@mui/icons-material/Add";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import DeleteIcon from "@mui/icons-material/Delete";
import CloseIcon from "@mui/icons-material/Close";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import { useState, useEffect, useRef } from "react";
import toast from "react-hot-toast";
import { axiosConfig } from "../../axios/axiosConfig";
import ProfitCardsTable from "./ProfitCardsTable";

// ─── helpers ─────────────────────────────────────────────────────────────────
const emptyMetric = () => ({ primary_value: "", suffix: "", order: 1 });
const emptySection = () => ({
  label: "OPTIONS",
  size: "half",
  order: 1,
  metrics: [emptyMetric()],
});
const emptyForm = () => ({
  title: "",
  date: new Date().toISOString().slice(0, 10),
  is_active: true,
  sections: [emptySection()],
});

// ─── shared form body ─────────────────────────────────────────────────────────
const CardForm = ({ form, setForm }) => {

  const updateSection = (si, field, value) =>
    setForm((prev) => {
      const sections = [...prev.sections];
      sections[si] = { ...sections[si], [field]: value };
      return { ...prev, sections };
    });

  const updateMetric = (si, mi, field, value) =>
    setForm((prev) => {
      const sections = [...prev.sections];
      const metrics = [...sections[si].metrics];
      metrics[mi] = { ...metrics[mi], [field]: value };
      sections[si] = { ...sections[si], metrics };
      return { ...prev, sections };
    });

  const addSection = () =>
    setForm((prev) => ({
      ...prev,
      sections: [
        ...prev.sections,
        { ...emptySection(), order: prev.sections.length + 1 },
      ],
    }));

  const removeSection = (si) =>
    setForm((prev) => ({
      ...prev,
      sections: prev.sections.filter((_, i) => i !== si),
    }));

  const removeMetric = (si, mi) =>
    setForm((prev) => {
      const sections = [...prev.sections];
      sections[si] = {
        ...sections[si],
        metrics: sections[si].metrics.filter((_, i) => i !== mi),
      };
      return { ...prev, sections };
    });

  return (
    <>
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={8}>
          <TextField
            label="Title"
            fullWidth
            size="small"
            value={form.title}
            onChange={(e) => setForm((p) => ({ ...p, title: e.target.value }))}
            placeholder="e.g. PROFIT BOOKED ON 18 MARCH"
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <TextField
            label="Date"
            type="date"
            fullWidth
            size="small"
            value={form.date}
            onChange={(e) => setForm((p) => ({ ...p, date: e.target.value }))}
            InputLabelProps={{ shrink: true }}
          />
        </Grid>
        <Grid item xs={12}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Switch
              checked={form.is_active}
              onChange={(e) =>
                setForm((p) => ({ ...p, is_active: e.target.checked }))
              }
              sx={{ "& .MuiSwitch-thumb": { background: "#016551" } }}
            />
            <Typography sx={{ fontSize: "13px", color: "#475569" }}>
              {form.is_active ? "Active" : "Inactive"}
            </Typography>
          </Box>
        </Grid>

      </Grid>

      <Divider sx={{ mb: 2 }} />

      {/* Sections */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 2,
        }}
      >
        <Typography
          sx={{ fontWeight: 700, fontSize: "14px", color: "#04343a" }}
        >
          Sections
        </Typography>
        <Button
          size="small"
          startIcon={<AddCircleOutlineIcon />}
          onClick={addSection}
          sx={{ color: "#016551", fontWeight: 600 }}
        >
          Add Section
        </Button>
      </Box>

      {form.sections.map((sec, si) => (
        <Box
          key={si}
          sx={{
            border: "1px solid #e2e8f0",
            borderRadius: "10px",
            p: 2,
            mb: 2,
            background: "#f8fafc",
          }}
        >
          <Box
            sx={{ display: "flex", gap: 1.5, mb: 2, alignItems: "flex-start" }}
          >
            <TextField
              label="Label"
              size="small"
              select
              value={sec.label}
              onChange={(e) => updateSection(si, "label", e.target.value)}
              sx={{ flex: 2 }}
            >
              {["OPTIONS", "FUTURES", "CASH"].map((opt) => (
                <MenuItem key={opt} value={opt}>
                  {opt}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              label="Size"
              size="small"
              select
              value={sec.size}
              onChange={(e) => updateSection(si, "size", e.target.value)}
              sx={{ flex: 1 }}
            >
              <MenuItem value="half">Half</MenuItem>
              <MenuItem value="full">Full</MenuItem>
            </TextField>
            <TextField
              label="Order"
              size="small"
              select
              value={sec.order}
              onChange={(e) =>
                updateSection(si, "order", Number(e.target.value))
              }
              sx={{ flex: 1 }}
            >
              {[1, 2, 3].map((n) => (
                <MenuItem key={n} value={n}>
                  {n}
                </MenuItem>
              ))}
            </TextField>
            <Tooltip title="Remove section">
              <IconButton
                size="small"
                onClick={() => removeSection(si)}
                disabled={form.sections.length === 1}
                sx={{ color: "#ef4444", mt: 0.3 }}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>

          <Typography
            sx={{ fontSize: "12px", fontWeight: 600, color: "#64748b", mb: 1 }}
          >
            Metrics
          </Typography>
          {sec.metrics.map((m, mi) => (
            <Box
              key={mi}
              sx={{ display: "flex", gap: 1.5, mb: 1, alignItems: "center" }}
            >
              <TextField
                label="Value"
                size="small"
                value={m.primary_value}
                onChange={(e) =>
                  updateMetric(si, mi, "primary_value", e.target.value)
                }
                sx={{ flex: 2 }}
                placeholder="₹12,420"
              />
              <TextField
                label="Suffix"
                size="small"
                value={m.suffix}
                onChange={(e) => updateMetric(si, mi, "suffix", e.target.value)}
                sx={{ flex: 2 }}
                placeholder="in 2 Lot"
              />
              <TextField
                label="Order"
                size="small"
                select
                value={m.order}
                onChange={(e) =>
                  updateMetric(si, mi, "order", Number(e.target.value))
                }
                sx={{ flex: 1 }}
              >
                {[1, 2, 3].map((n) => (
                  <MenuItem key={n} value={n}>
                    {n}
                  </MenuItem>
                ))}
              </TextField>
              <IconButton
                size="small"
                onClick={() => removeMetric(si, mi)}
                disabled={sec.metrics.length === 1}
                sx={{ color: "#ef4444" }}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Box>
          ))}
        </Box>
      ))}
    </>
  );
};

// ─── main page ────────────────────────────────────────────────────────────────
const ProfitCards = () => {
  const [cards, setCards] = useState([]);
  const [tableLoading, setTableLoading] = useState(false);

  // create dialog
  const [createOpen, setCreateOpen] = useState(false);
  const [createForm, setCreateForm] = useState(emptyForm());
  const [creating, setCreating] = useState(false);

  // edit dialog
  const [editOpen, setEditOpen] = useState(false);
  const [editForm, setEditForm] = useState(emptyForm());
  const [editCard, setEditCard] = useState(null);
  const [editing, setEditing] = useState(false);

  // image upload dialog
  const [uploadOpen, setUploadOpen] = useState(false);
  const [uploadCard, setUploadCard] = useState(null);
  const [uploadImage, setUploadImage] = useState(null);
  const [uploading, setUploading] = useState(false);
  const uploadFileRef = useRef();

  const fetchCards = async () => {
    setTableLoading(true);
    try {
      const res = await axiosConfig({
        method: "get",
        url: "/advertisement/profit-cards/",
      });
      setCards(res?.data || []);
    } catch (err) {
      toast.error(
        err?.response?.data?.response || err.message || "Failed to fetch",
      );
    } finally {
      setTableLoading(false);
    }
  };

  useEffect(() => {
    fetchCards();
  }, []);

  // ── create ────────────────────────────────────────────────────────────────
  const handleCreate = async () => {
    if (!createForm.title.trim()) return toast.error("Title is required");
    if (!createForm.date) return toast.error("Date is required");
    setCreating(true);
    try {
      await axiosConfig({
        method: "post",
        url: "/advertisement/profit-cards/",
        data: createForm,
      });
      toast.success("Profit card created");
      setCreateOpen(false);
      setCreateForm(emptyForm());
      fetchCards();
    } catch (err) {
      toast.error(
        err?.response?.data?.response || err.message || "Failed to create",
      );
    } finally {
      setCreating(false);
    }
  };

  // ── open edit ─────────────────────────────────────────────────────────────
  const openEdit = (card) => {
    setEditCard(card);
    setEditForm({
      title: card.title,
      date: card.date,
      is_active: card.is_active,
      sections: card.sections || [emptySection()],
    });

    setEditOpen(true);
  };

  // ── edit submit ───────────────────────────────────────────────────────────
  const handleEdit = async () => {
    if (!editForm.title.trim()) return toast.error("Title is required");
    if (!editForm.date) return toast.error("Date is required");
    setEditing(true);
    try {
      await axiosConfig({
        method: "put",
        url: `/advertisement/profit-cards/${editCard.id}/`,
        data: {
          title: editForm.title,
          date: editForm.date,
          is_active: editForm.is_active,
          sections: editForm.sections,
        },
      });


      toast.success("Profit card updated");
      setEditOpen(false);
      fetchCards();
    } catch (err) {
      toast.error(
        err?.response?.data?.response || err.message || "Failed to update",
      );
    } finally {
      setEditing(false);
    }
  };

  // ── delete (soft — PATCH is_active false) ─────────────────────────────────
  const handleToggleActive = async (card) => {
    try {
      await axiosConfig({
        method: "patch",
        url: `/advertisement/profit-cards/${card.id}/`,
        data: { is_active: !card.is_active },
      });
      toast.success(card.is_active ? "Card deactivated" : "Card activated");
      fetchCards();
    } catch (err) {
      toast.error(
        err?.response?.data?.response || err.message || "Failed to update",
      );
    }
  };

  const openUpload = (card) => { setUploadCard(card); setUploadImage(null); setUploadOpen(true); };
  const closeUpload = () => { if (!uploading) { setUploadOpen(false); setUploadCard(null); setUploadImage(null); } };

  const handleUpload = async () => {
    if (!uploadImage) return toast.error("Please select an image");
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("image", uploadImage);
      await axiosConfig({
        method: "put",
        url: `/advertisement/profit-cards/${uploadCard.id}/upload-image/`,
        data: fd,
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Image uploaded successfully");
      closeUpload();
      fetchCards();
    } catch (err) {
      toast.error(err?.response?.data?.response || err.message || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const closeCreate = () => {
    if (!creating) {
      setCreateOpen(false);
      setCreateForm(emptyForm());
    }
  };
  const closeEdit = () => {
    if (!editing) {
      setEditOpen(false);
      setEditCard(null);
    }
  };

  return (
    <>
      <Box sx={{ p: 3, background: "#eef2f7", minHeight: "100vh" }}>
        {/* Page Header */}
        <Box
          sx={{
            background: "#04343a",
            borderRadius: "12px",
            boxShadow: "0 4px 20px rgba(4,52,58,0.3)",
            p: "20px 28px",
            mb: 3,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <StyleIcon sx={{ color: "#fff", fontSize: 32 }} />
            <Box>
              <Typography
                sx={{ color: "#fff", fontWeight: 700, fontSize: "20px" }}
              >
                Profit Cards
              </Typography>
              <Typography
                sx={{ color: "rgba(255,255,255,0.65)", fontSize: "13px" }}
              >
                Create and manage profit showcase cards
              </Typography>
            </Box>
          </Box>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setCreateOpen(true)}
            sx={{
              background: "#016551",
              borderRadius: "8px",
              fontWeight: 600,
              px: 3,
              boxShadow: "0 4px 12px rgba(1,101,81,0.3)",
              "&:hover": {
                background: "linear-gradient(135deg, #1db954 0%, #17a349 100%)",
                boxShadow: "0 6px 16px rgba(1,101,81,0.4)",
              },
            }}
          >
            Create Card
          </Button>
        </Box>

        <ProfitCardsTable
          cards={cards}
          loading={tableLoading}
          onEdit={openEdit}
          onToggleActive={handleToggleActive}
          onUploadImage={openUpload}
        />
      </Box>

      {/* ── Create Dialog ─────────────────────────────────────────────────── */}
      <Dialog
        open={createOpen}
        onClose={closeCreate}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { borderRadius: "12px" } }}
      >
        <DialogTitle
          sx={{
            background: "#04343a",
            color: "#fff",
            fontWeight: 700,
            fontSize: "17px",
          }}
        >
          Create Profit Card
        </DialogTitle>
        <IconButton
          onClick={closeCreate}
          sx={{ position: "absolute", right: 8, top: 8, color: "#fff" }}
        >
          <CloseIcon />
        </IconButton>
        <DialogContent sx={{ pt: 3 }}>
          <CardForm form={createForm} setForm={setCreateForm} />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={closeCreate} sx={{ color: "#64748b" }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleCreate}
            disabled={creating}
            sx={{
              background: "linear-gradient(135deg, #016551, #014d3d)",
              borderRadius: "8px",
              fontWeight: 600,
              px: 4,
              "&:hover": {
                background: "linear-gradient(135deg, #1db954 0%, #17a349 100%)",
              },
            }}
          >
            {creating ? "Creating..." : "Create Card"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Edit Dialog ───────────────────────────────────────────────────── */}
      <Dialog
        open={editOpen}
        onClose={closeEdit}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { borderRadius: "12px" } }}
      >
        <DialogTitle
          sx={{
            background: "#04343a",
            color: "#fff",
            fontWeight: 700,
            fontSize: "17px",
          }}
        >
          Edit Profit Card
        </DialogTitle>
        <IconButton
          onClick={closeEdit}
          sx={{ position: "absolute", right: 8, top: 8, color: "#fff" }}
        >
          <CloseIcon />
        </IconButton>
        <DialogContent sx={{ pt: 3 }}>
          <CardForm form={editForm} setForm={setEditForm} />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={closeEdit} sx={{ color: "#64748b" }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleEdit}
            disabled={editing}
            sx={{
              background: "linear-gradient(135deg, #016551, #014d3d)",
              borderRadius: "8px",
              fontWeight: 600,
              px: 4,
              "&:hover": {
                background: "linear-gradient(135deg, #1db954 0%, #17a349 100%)",
              },
            }}
          >
            {editing ? "Saving..." : "Save Changes"}
          </Button>
        </DialogActions>
      </Dialog>
      {/* ── Upload Image Dialog ───────────────────────────────────────────── */}
      <Dialog open={uploadOpen} onClose={closeUpload} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: "12px" } }}>
        <DialogTitle sx={{ background: "#04343a", color: "#fff", fontWeight: 700, fontSize: "17px" }}>
          Upload Image
        </DialogTitle>
        <IconButton onClick={closeUpload} sx={{ position: "absolute", right: 8, top: 8, color: "#fff" }}>
          <CloseIcon />
        </IconButton>
        <DialogContent sx={{ pt: 3 }}>
          <input
            type="file"
            accept="image/*"
            ref={uploadFileRef}
            style={{ display: "none" }}
            onChange={(e) => setUploadImage(e.target.files[0] || null)}
          />
          <Box
            onClick={() => uploadFileRef.current.click()}
            sx={{
              border: "2px dashed #e2e8f0",
              borderRadius: "10px",
              p: 4,
              textAlign: "center",
              cursor: "pointer",
              background: uploadImage ? "#f0faf7" : "#f8fafc",
              "&:hover": { borderColor: "#016551", background: "#f0faf7" },
              transition: "all 0.2s",
            }}
          >
            <UploadFileIcon sx={{ fontSize: 36, color: uploadImage ? "#016551" : "#94a3b8", mb: 1 }} />
            <Typography sx={{ fontSize: "13px", color: uploadImage ? "#016551" : "#64748b", fontWeight: 600 }}>
              {uploadImage ? uploadImage.name : "Click to select an image"}
            </Typography>
            {!uploadImage && (
              <Typography sx={{ fontSize: "11px", color: "#94a3b8", mt: 0.5 }}>
                PNG, JPG, WEBP supported
              </Typography>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={closeUpload} sx={{ color: "#64748b" }}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleUpload}
            disabled={uploading || !uploadImage}
            sx={{
              background: "linear-gradient(135deg, #016551, #014d3d)",
              borderRadius: "8px",
              fontWeight: 600,
              px: 4,
              "&:hover": { background: "linear-gradient(135deg, #1db954 0%, #17a349 100%)" },
            }}
          >
            {uploading ? "Uploading..." : "Upload"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ProfitCards;
